import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Users, Key, GraduationCap } from 'lucide-react';
import { getUser } from '../lib/auth';
import MemberManagement from './MemberManagement';
import UserManagement from './UserManagement';
import APICredentialsManagement from './APICredentialsManagement';
import SensorManagement from './SensorManagement';

type Tab = 'members' | 'users' | 'sensors' | 'api';

export default function MembersAndUsers() {
  const currentUser = getUser();
  const canManageUsers = currentUser?.role === 'admin' || currentUser?.role === 'systemadmin';
  const canManageAPI = currentUser?.role === 'admin' || currentUser?.role === 'systemadmin';

  const [searchParams, setSearchParams] = useSearchParams();

  // Read tab from URL or default to 'members'
  const tabParam = searchParams.get('tab') as Tab | null;
  const memberIdParam = searchParams.get('member_id');

  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam && ['members', 'users', 'sensors', 'api'].includes(tabParam) ? tabParam : 'members'
  );
  const [filterMemberId, setFilterMemberId] = useState<string | null>(memberIdParam);

  // Update state when URL params change
  useEffect(() => {
    if (tabParam && ['members', 'users', 'sensors', 'api'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    setFilterMemberId(memberIdParam);
  }, [tabParam, memberIdParam]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    // Clear member filter when switching tabs (unless going to users)
    if (tab !== 'users') {
      setFilterMemberId(null);
      setSearchParams({ tab });
    } else {
      if (filterMemberId) {
        setSearchParams({ tab, member_id: filterMemberId });
      } else {
        setSearchParams({ tab });
      }
    }
  };

  const clearMemberFilter = () => {
    setFilterMemberId(null);
    setSearchParams({ tab: 'users' });
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => handleTabChange('members')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'members'
                  ? 'border-kf-primary text-kf-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building2 size={18} />
              Medlemsorganisasjoner
            </button>
            {canManageUsers && (
              <button
                onClick={() => handleTabChange('users')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === 'users'
                    ? 'border-kf-primary text-kf-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Users size={18} />
                Brukere
              </button>
            )}
            {canManageUsers && (
              <button
                onClick={() => handleTabChange('sensors')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === 'sensors'
                    ? 'border-kf-primary text-kf-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <GraduationCap size={18} />
                Sensorer
              </button>
            )}
            {canManageAPI && (
              <button
                onClick={() => handleTabChange('api')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === 'api'
                    ? 'border-kf-primary text-kf-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Key size={18} />
                API
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'members' && <MemberManagement />}
      {activeTab === 'users' && canManageUsers && (
        <UserManagement
          filterMemberId={filterMemberId}
          onClearFilter={clearMemberFilter}
        />
      )}
      {activeTab === 'sensors' && canManageUsers && <SensorManagement />}
      {activeTab === 'api' && canManageAPI && <APICredentialsManagement />}
    </div>
  );
}
